import { Component, OnInit, Inject } from '@angular/core';
import { environment } from 'src/app/environment';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { MyApiService } from '../../custom/my-api.service';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'app-blogs-details',
    templateUrl: './blogs-details.component.html',
    styleUrls: ['./blogs-details.component.scss'],
    standalone: false
})
export class BlogsDetailsComponent implements OnInit {
    blog: any;
    readonly baseUrl = environment.APP_URL;
    fileUrl = environment.FILE_URL;

    constructor(
        private readonly titleService: Title,
        private readonly metaService: Meta,
        private readonly route: ActivatedRoute,
        private readonly apiService: MyApiService,
        @Inject(DOCUMENT) private readonly document: Document
    ) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            if (params['blog_id']) {
                this.apiService.getBlogById(params['blog_id']).subscribe(
                    (response: any) => {
                        this.blog = response.data;

                        if (this.blog?.metaInputs?.length > 0) {
                            const meta = this.blog.metaInputs[0];
                            this.titleService.setTitle(meta.title);

                            // Use both approaches for maximum compatibility
                            // 1. Use Meta service for Angular's internal tracking
                            this.metaService.updateTag({ name: 'description', content: meta.desc }, "name='description'");
                            this.metaService.updateTag({ name: 'keywords', content: meta.keywords }, "name='keywords'");
                            this.metaService.updateTag({ name: 'title', content: meta.title }, "name='title'");
                            this.metaService.updateTag({ property: 'og:title', content: meta.ogTitle ?? meta.title }, "property='og:title'");
                            this.metaService.updateTag({ property: 'og:description', content: meta.ogDesc ?? meta.desc }, "property='og:description'");
                            this.metaService.updateTag({ property: 'og:url', content: meta.ogURL }, "property='og:url'");
                            if (meta.ogImageURL) {
                                this.metaService.updateTag({ property: 'og:image', content: meta.ogImageURL }, "property='og:image'");
                            }

                            // 2. Direct DOM manipulation for ensuring tags are visible in page source
                            this.setMetaTag('description', meta.desc);
                            this.setMetaTag('keywords', meta.keywords);
                            this.setMetaTag('title', meta.title);
                            this.setMetaTag('og:title', meta.ogTitle ?? meta.title, true);
                            this.setMetaTag('og:description', meta.ogDesc ?? meta.desc, true);
                            this.setMetaTag('og:url', meta.ogURL, true);
                            if (meta.ogImageURL) {
                                this.setMetaTag('og:image', meta.ogImageURL, true);
                            }
                        }

                        // Process blog.description to prepend fileUrl to <img src> if needed
                        if (this.blog?.description) {
                            this.blog.description = this.blog.description.replace(/<img([^>]+)src=["'](\.\.\/mediaFiles?\/[^"'>]+)["']([^>]*)>/gi,
                                (match: string, before: string, src: string, after: string) => {
                                // Only prepend if not already absolute or starting with fileUrl
                                if (!src.startsWith(this.fileUrl) && !/^https?:\/\//.test(src)) {
                                    return `<img${before}src="${this.fileUrl}/${src.replace(/^\.\.\//, '')}"${after}>`;
                                }
                                return match;
                            });
                        }
                    }
                );
            }
        });
    }

    /**
     * Sets a meta tag directly in the document head to ensure it's visible in page source
     * @param name The name or property of the meta tag
     * @param content The content value of the meta tag
     * @param isProperty Whether this is a property attribute (for Open Graph) or name attribute
     */
    private setMetaTag(name: string, content: string, isProperty: boolean = false): void {
        if (!content) return;

        const attr = isProperty ? 'property' : 'name';
        const selector = `meta[${attr}="${name}"]`;

        // Check if the meta tag already exists
        let metaElement = this.document.head.querySelector(selector);

        if (metaElement) {
            // Update existing tag
            metaElement.setAttribute('content', content);
        } else {
            // Create a new meta tag
            metaElement = this.document.createElement('meta');
            metaElement.setAttribute(attr, name);
            metaElement.setAttribute('content', content);
            this.document.head.appendChild(metaElement);
        }
    }
}
